import React, {useCallback, useMemo, useRef, useState} from 'react';

import {BulkActionFooter} from 'src/components/bulk-tool/bulk-action-footer';
import {WidthItem} from 'src/components/width-item';
import {useNewsletterDictionary} from 'src/hooks/dictionaries/useNewsletterDictionary';
import {sortNewsletterItems} from 'src/utils/sortNewsletterItems';

import Button from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import {ComboBox, ComboboxItem} from '@pb/kit-component-combo-box';
import {DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';

import {TOOL_TITLE} from './constants';

import type {ProcessBulkAction} from 'src/components/bulk-tool/typedef';
import type {ActionOptions} from 'src/components/save-with-reason/action-with-reason-button';
import type {UpdateUserNewsletter} from 'src/openapi/admin/models/UpdateUserNewsletter';
import type {CheckBoxChangeData} from 'src/typedef';


type Props = {
    userIds: number[],
    onCloseModal: (submit: boolean) => void,
    processAction: ProcessBulkAction<UpdateUserNewsletter>,
};

export const UpdateUserNewsletterModal = ({userIds, onCloseModal, processAction}: Props) => {
    const [selectedNewsletters, setSelectedNewsLetters] = useState<string[]>([]);

    const {newsletterDictionary} = useNewsletterDictionary();

    const handleGroupToggle = useCallback((childIds: string[], defaultId: string, checked: boolean) => {
        setSelectedNewsLetters(prev => checked
            ? [...new Set([...prev, defaultId])].sort()
            : prev.filter(c => !childIds.includes(c)),
        );
    }, []);

    const handleGroupChildSelect = useCallback((childIds: string[], code: string) => {
        setSelectedNewsLetters(prev => [
            ...prev.filter(c => !childIds.includes(c)),
            code,
        ].sort());
    }, []);

    const handleLeafToggle = useCallback((code: string, checked: boolean) => {
        setSelectedNewsLetters(prev => (checked
            ? [...prev, code]
            : prev.filter(c => c !== code)
        ).sort());
    }, []);

    const comboboxGroupIndex = useMemo(() =>
        newsletterDictionary.findIndex(item => (item.children?.length ?? 0) > 1),
    [newsletterDictionary]);

    const lastComboValueRef = useRef<string>('');

    const newsletterOptions = useMemo(() => newsletterDictionary.flatMap((item, index) => {
        const childIds = item.children?.map(c => c.id) ?? [];
        if (childIds.length > 1 && index === comboboxGroupIndex) {
            const selectedChild = selectedNewsletters.find(c => childIds.includes(c));
            const defaultChildId = item.children![0].id;
            const comboValue = selectedChild ?? (lastComboValueRef.current || defaultChildId);
            if (selectedChild) {
                lastComboValueRef.current = selectedChild;
            }
            const sortedChildren = sortNewsletterItems(item.children!);
            return [(
                <Group direction="v" gap="xs" key={item.id}>
                    <CheckBox
                        label={item.description}
                        checked={!!selectedChild}
                        onCheckBoxChange={({event}: CheckBoxChangeData) =>
                            handleGroupToggle(childIds, comboValue, event.currentTarget.checked)}
                    />
                    <WidthItem width={300}>
                        <ComboBox
                            disabled={!selectedChild}
                            value={comboValue}
                            onChange={(code: string) => handleGroupChildSelect(childIds, code)}>
                            {sortedChildren.map(child => (
                                <ComboboxItem key={child.id} caption={child.description} value={child.id}/>
                            ))}
                        </ComboBox>
                    </WidthItem>
                </Group>
            )];
        }
        if (childIds.length) {
            const sortedChildren = sortNewsletterItems(item.children!);
            return (
                <Group key={item.id} style={{paddingTop: 16}} direction="v" gap="m"> {sortedChildren.map(child => (
                    <CheckBox
                        key={child.id}
                        name={child.id}
                        label={child.description}
                        checked={selectedNewsletters.includes(child.id)}
                        onCheckBoxChange={({event}: CheckBoxChangeData) =>
                            handleLeafToggle(child.id, event.currentTarget.checked)}
                    />
                ))}
                </Group>
            );
        }
        return [(
            <CheckBox
                key={item.id}
                name={item.id}
                label={item.description}
                checked={selectedNewsletters.includes(item.id)}
                onCheckBoxChange={({event}: CheckBoxChangeData) =>
                    handleLeafToggle(item.id, event.currentTarget.checked)}
            />
        )];
    }), [newsletterDictionary, comboboxGroupIndex, selectedNewsletters,
        handleGroupToggle, handleGroupChildSelect, handleLeafToggle]);

    const updateNewsletters = useCallback(async (options?: ActionOptions) => {
        return await processAction({newsletterTypes: selectedNewsletters, usersIds: userIds}, options);
    }, [selectedNewsletters, userIds, processAction]);

    return (
        <>
            <DialogHeader onClose={() => onCloseModal(false)}>
                {TOOL_TITLE}
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                <Group direction="v">
                    <WidthItem width="100%">
                        Update a newsletter subscription for selected users.
                    </WidthItem>
                    <WidthItem width={300}>
                        <Label>PitchBook newsletter</Label>
                        <Group direction="v" gap="s">
                            {newsletterOptions}
                        </Group>
                    </WidthItem>
                </Group>
            </DialogMain>
            <BulkActionFooter action="Update" onConfirm={updateNewsletters}>
                <Group>

                    <Button variant="Type.SECONDARY" onClick={() => onCloseModal(false)}>
                        Cancel
                    </Button>
                </Group>
            </BulkActionFooter>
        </>
    );
};
